/**
 * Server Status Utilities
 *
 * Mirrors Java dview's Server.checkIsAlive() and AuthHelper.isAccessible()
 * for determining server online/offline/firewall status.
 */

import axios from 'axios';

// Maximum age for a server to be considered "alive" based on heartbeat timestamp
// Java dview uses 36000 seconds (10 hours)
const ALIVE_THRESHOLD_SECONDS = 36000;

// Timeout for an accessibility probe. Lowered 5s→2.5s (#86bb7phhh): an UNREACHABLE member should fail
// FAST so it doesn't hold a connection for a full 5s during the bounded background status sweep; a
// reachable member answers info.isAlive well under this. Combined with vantage-ordering (try the
// likely-reachable IP first) the wrong-side probe is usually skipped entirely.
// Exported so the login-target probe (LoginView.testServerConnectivity) shares ONE timeout constant
// instead of hardcoding its own 5s/1s list (#86bb7phhh).
export const ACCESSIBILITY_TIMEOUT_MS = 2500;

/**
 * Check if server is "alive" based on its last heartbeat timestamp
 * Mirrors Java dview's Server.checkIsAlive()
 *
 * @param {number} bTimestamp - Unix timestamp of server's last heartbeat
 * @returns {boolean} - True if timestamp is within 10 hours
 */
export const checkIsAlive = (bTimestamp) => {
  if (!bTimestamp || bTimestamp === 0) {
    return false;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const ageSeconds = nowSeconds - bTimestamp;

  return ageSeconds < ALIVE_THRESHOLD_SECONDS;
};

/**
 * Check if server is directly accessible via RPC call
 * Mirrors Java dview's AuthHelper.isAccessible()
 *
 * @param {string} serverIP - Server IP or hostname
 * @param {number} serverPort - Server port (default 80)
 * @returns {Promise<boolean>} - True if server responds to info.isAlive
 */
export const checkIsAccessible = async (serverIP, serverPort = 80, signal = undefined) => {
  if (!serverIP) {
    return false;
  }
  // Already aborted before we even started → don't open a connection at all. #86bb7phhh
  if (signal && signal.aborted) {
    return false;
  }

  // In dev mode, route through /server-check/ proxy to avoid CORS
  const isDev = process.env.NODE_ENV === 'development';
  const hostPort = serverPort && serverPort !== 80 ? `${serverIP}:${serverPort}` : serverIP;
  const url = isDev
    ? `/server-check/${hostPort}/JSON/`
    : `http://${serverIP}:${serverPort}/JSON/`;

  // axios ^0.19.2 (pinned) has NO AbortController/`signal` support — its cancellation mechanism is
  // CancelToken. Translate the caller's optional AbortSignal into a CancelToken so an abort() actually
  // CANCELS the in-flight request and frees the browser connection IMMEDIATELY — not merely stops the
  // next probe from launching. This is what lets a login submit reclaim the ~6-conn/host pool from the
  // background status sweep instead of waiting out each probe's 2.5s timeout. #86bb7phhh
  const config = { timeout: ACCESSIBILITY_TIMEOUT_MS };
  let cancelSource = null;
  let onAbort = null;
  if (signal && typeof axios.CancelToken !== 'undefined' && axios.CancelToken.source) {
    cancelSource = axios.CancelToken.source();
    config.cancelToken = cancelSource.token;
    onAbort = () => cancelSource.cancel('aborted');
    if (signal.addEventListener) signal.addEventListener('abort', onAbort);
  }

  try {
    const response = await axios.post(url, {
      jsonrpc: 2.0,
      method: 'info.isAlive',
      params: [],
      id: 999
    }, config);

    // Check if we got a valid response (info.isAlive returns [true, true])
    const result = response.data?.result;
    return !!(result && (result === true || (Array.isArray(result) && result[0] === true)));
  } catch (e) {
    // Network error, timeout, cancelled, or server not responding
    return false;
  } finally {
    if (onAbort && signal && signal.removeEventListener) signal.removeEventListener('abort', onAbort);
  }
};

// ── Vantage-ordered accessibility (#86bb7phhh) ───────────────────────────────
// A member NVR is reachable at EITHER its LAN IP (sLocalIP) or its public IP (sIP) depending on where
// the CLIENT sits. Probing the wrong one first burns a full timeout before the fallback — brutal at
// 100+ members. Browsers hide the client's own LAN IP, so we infer the client "vantage" from the
// address we reach the controller on (page host in prod; the proxied controller in dev): a private
// vantage ⇒ we're inside a LAN ⇒ try LAN IPs (same /24) first; a public vantage ⇒ we're remote ⇒ try
// public IPs first. We ALWAYS fall back to the other — this only reorders, never assumes.

export const isPrivateHost = (host) =>
  /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(String(host || '').trim());

const subnet24 = (ip) => {
  const m = String(ip || '').match(/^(\d{1,3}\.\d{1,3}\.\d{1,3})\./);
  return m ? m[1] : null;
};

const isProbableAddress = (ip) =>
  !!ip && ip !== '127.0.0.1' && !String(ip).includes('error');

// The address we reach the controller on = our network vantage. Prod: the page host. Dev: the proxied
// controller (REACT_APP_SERVER_HOST), since the page host is just localhost.
export const getClientVantageHost = () => {
  try {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_SERVER_HOST) {
      return String(process.env.REACT_APP_SERVER_HOST).replace(/:\d+$/, '');
    }
    if (typeof window !== 'undefined' && window.location) return window.location.hostname;
  } catch (e) { /* ignore */ }
  return '';
};

// Order [preferred, fallback] candidate IPs for a member, by client vantage. Never assumes: both valid
// IPs are returned, just ordered so the likely-reachable one is tried first.
export const orderCandidateIps = (server, vantageHost) => {
  const local = server.sLocalIP;
  const ext = server.sIP;
  const vantPrivate = isPrivateHost(vantageHost);
  const localSameLan = vantPrivate && subnet24(local) && subnet24(local) === subnet24(vantageHost);
  const ordered = localSameLan ? [local, ext] : [ext, local];   // remote OR cross-subnet ⇒ public first
  return ordered.filter(isProbableAddress);
};

// Probe a member's DIRECT reachability from this client, trying the vantage-preferred IP first and
// falling back to the other. Returns true on the first success. `signal` cancels in-flight probes.
export const probeServerAccessible = async (server, { signal, vantageHost } = {}) => {
  const vantage = vantageHost !== undefined ? vantageHost : getClientVantageHost();
  const candidates = orderCandidateIps(server, vantage);
  for (let i = 0; i < candidates.length; i++) {
    if (signal && signal.aborted) return false;
    // eslint-disable-next-line no-await-in-loop
    if (await checkIsAccessible(candidates[i], server.bPort || 80, signal)) return true;
  }
  return false;
};

/**
 * Server status enum
 */
export const ServerStatus = {
  ONLINE: 'online',           // Accessible and alive
  OFFLINE: 'offline',         // Not alive (old timestamp) and not accessible
  FIREWALL: 'firewall',       // Alive (recent timestamp) but not accessible
  UNKNOWN: 'unknown'          // Status not yet determined
};

/**
 * Determine comprehensive server status
 *
 * Logic:
 * - ONLINE: Server is both alive (recent heartbeat) AND accessible (responds to RPC)
 * - FIREWALL: Server is alive (recent heartbeat) BUT not accessible (behind firewall)
 * - OFFLINE: Server is not alive (old/no timestamp) - don't bother checking accessibility
 *
 * @param {object} server - Server object with bTimestamp, sIP, bPort
 * @param {boolean} skipAccessibilityCheck - If true, only check timestamp (faster)
 * @returns {Promise<string>} - ServerStatus enum value
 */
export const getServerStatus = async (server, skipAccessibilityCheck = false) => {
  if (!server) {
    return ServerStatus.UNKNOWN;
  }

  const isAlive = checkIsAlive(server.bTimestamp);

  // If not alive based on timestamp, it's offline - no need to check accessibility
  if (!isAlive) {
    return ServerStatus.OFFLINE;
  }

  // If skipping accessibility check, assume online if alive
  if (skipAccessibilityCheck) {
    return ServerStatus.ONLINE;
  }

  // Server is alive, check if accessible
  const isAccessible = await checkIsAccessible(server.sIP, server.bPort || 80);

  if (isAccessible) {
    return ServerStatus.ONLINE;
  } else {
    // Alive but not accessible = behind firewall
    return ServerStatus.FIREWALL;
  }
};

/**
 * Check status of multiple servers in parallel
 *
 * @param {array} servers - Array of server objects
 * @param {boolean} skipAccessibilityCheck - If true, only check timestamps
 * @returns {Promise<object>} - Map of bSerial to ServerStatus
 */
export const checkAllServerStatus = async (servers, skipAccessibilityCheck = false) => {
  if (!servers || servers.length === 0) {
    return {};
  }

  const statusPromises = servers.map(async (server) => {
    const status = await getServerStatus(server, skipAccessibilityCheck);
    return { bSerial: server.bSerial, status };
  });

  const results = await Promise.all(statusPromises);

  const statusMap = {};
  results.forEach(({ bSerial, status }) => {
    statusMap[bSerial] = status;
  });

  return statusMap;
};

/**
 * Format timestamp as human-readable "time ago" string
 *
 * @param {number} bTimestamp - Unix timestamp
 * @returns {string} - Human readable string like "2 hours ago"
 */
export const formatTimestampAge = (bTimestamp) => {
  if (!bTimestamp || bTimestamp === 0) {
    return 'Never';
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const ageSeconds = nowSeconds - bTimestamp;

  if (ageSeconds < 60) {
    return 'Just now';
  } else if (ageSeconds < 3600) {
    const minutes = Math.floor(ageSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(ageSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
};
